// The measured roughness of the bare ground (S4; pipeline/rough.py, bake2
// finish.rough, meta.surface.rough): one RGB cell per `cell_m` (32 m) of the
// world frame, row 0 = the north edge (z 0), col 0 = the west edge (x 0):
//   R relief      RMS of the pinned DEM's 2–4 m micro-relief (m)
//   G scale       log2(RMS 4–8 m / RMS 1–2 m): blocky > fine talus
//   B contrast    the ortho's 1–5 m band contrast (RMS / mean, MEADOW-P c_mid)
// log-coded bytes (meta enc: x = lo + (v − 1)(hi − lo) / 254, 2^x for a log2
// channel; 0 = no value: that source was not measured — the tier rule).
//
// What it drives here is RELATIVE to ROUGH_REF: the values the phase-1 look
// (grass.js CLASS_SCATTER, the scree / rock materials) sits at — the medians
// of the reference measurement (S4, St. Moritz high scree / rock, 2026-10-18),
// a calibration point, never a place. At the reference, and wherever the
// grid has no value (absent file = a bake from the global tier: meta
// source 'default'), every factor is exactly 1: the phase-1 look.
export const ROUGH_REF = {
  scree: { relief_m: 0.058, scale: 0.93, contrast: 0.100 },
  rock: { relief_m: 0.19, scale: 1.42, contrast: 0.176 },
};
// the stone clumps (grass.js): density never above the phase-1 density (the
// scatter's MAX_C is already near full on reference scree), so a boulder field
// reads through bigger stones and fuller clumps, fine talus through fewer,
// smaller ones
export const STONE_DENS = [0.35, 1];
export const STONE_SIZE = [0.6, 1.8];
export const STONE_COUNT = [0.6, 1.5];
// the ground layers' contrast factor k (groundmat.js uRough): the detail
// multiplier, cavity and normal deviate from x1 / flat k times as far — the
// mean stays x1. Channel c of the k grid = layer GROUND_K_LAYERS[c]; never
// the cliff (its scan is the face, not the ground's measure)
export const GROUND_K = [0.6, 1.5];
export const GROUND_K_LAYERS = ['scree', 'rock'];

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

// meta.surface.rough -> {w, h, cell, enc, data (RGB bytes)} | null (no file,
// a default source, no base url, a failed fetch / decode — warned). One load
// per file: the stones (grass.js) and the ground (main.js) share it.
let _memo = null;
export function loadRough(baseUrl, meta) {
  const r = meta && meta.surface && meta.surface.rough;
  if (!baseUrl || !r || !r.file || !(r.cell_m > 0)) return Promise.resolve(null);
  const url = `${baseUrl}/${r.file}`;
  if (!_memo || _memo.url !== url) _memo = { url, p: _load(url, r) };
  return _memo.p;
}
async function _load(url, r) {
  // surface.js (lazy: its worker / JSON imports stay out of plain-node users)
  const { decodeImage } = await import('./surface.js');
  const d = await decodeImage(url, 3, 'bulk');
  if (d.status !== 200 || d.w !== r.w || d.h !== r.h) {
    console.warn(`[rough] ${r.file}: ${d.status === 200 ? `${d.w}x${d.h}, meta says ${r.w}x${r.h}` : `status ${d.status}`} — the default look`);
    return null;
  }
  return { w: r.w, h: r.h, cell: r.cell_m, enc: r.enc, data: d.data };
}

function dec(e, v) {
  const x = e.lo + (v - 1) * (e.hi - e.lo) / 254;
  return e.log2 ? Math.pow(2, x) : x;
}

// the grid at (x, z): bilinear in code space (log-linear) between the cell
// centres -> {relief, scale, contrast} (null per channel without a value) | null
export function roughAt(R, x, z) {
  if (!R) return null;
  const fx = clamp(x / R.cell - 0.5, 0, R.w - 1), fz = clamp(z / R.cell - 0.5, 0, R.h - 1);
  const i0 = Math.floor(fx), j0 = Math.floor(fz);
  const i1 = Math.min(R.w - 1, i0 + 1), j1 = Math.min(R.h - 1, j0 + 1);
  const ax = fx - i0, az = fz - j0, D = R.data;
  const out = {};
  ['relief', 'scale', 'contrast'].forEach((k, ch) => {
    const a = D[(j0 * R.w + i0) * 3 + ch], b = D[(j0 * R.w + i1) * 3 + ch];
    const c = D[(j1 * R.w + i0) * 3 + ch], d = D[(j1 * R.w + i1) * 3 + ch];
    if (!a || !b || !c || !d) { out[k] = null; return; }
    const v = (a * (1 - ax) + b * ax) * (1 - az) + (c * (1 - ax) + d * ax) * az;
    out[k] = dec(R.enc[['r', 'g', 'b'][ch]], v);
  });
  return out;
}

// a host class's stone factors at a sample -> {dens, size, count} | null (no
// host class, no relief value: the phase-1 scatter, untouched)
export function stoneTune(key, s) {
  const ref = ROUGH_REF[key];
  if (!ref || !s || !(s.relief > 0)) return null;
  const f = Math.sqrt(s.relief / ref.relief_m);
  const cnt = s.scale === null ? 1 : Math.pow(2, (ref.scale - s.scale) / 2);
  return {
    dens: clamp(f, STONE_DENS[0], STONE_DENS[1]),
    size: clamp(f, STONE_SIZE[0], STONE_SIZE[1]),
    count: clamp(cnt, STONE_COUNT[0], STONE_COUNT[1]),
  };
}

// a host layer's ground k at a sample -> k | 1 (no value: the phase-1 look).
// scree: the photo's 1–5 m contrast over the layer's own (manifest stats
// c_mid, cMid) — the drawn multiplier carries the measured contrast; rock:
// the relief over ROUGH_REF, square root (a rock photo's contrast is cliff
// shading, not texture: the layer's c_mid 0.03 against 0.18 measured)
export function groundK(key, s, cMid) {
  let k = 1;
  if (key === 'scree' && s && s.contrast > 0 && cMid > 0) k = s.contrast / cMid;
  else if (key === 'rock' && s && s.relief > 0) k = Math.sqrt(s.relief / ROUGH_REF.rock.relief_m);
  return clamp(k, GROUND_K[0], GROUND_K[1]);
}

// the k grid groundmat.js uploads: per cell one byte per GROUND_K_LAYERS
// channel, (k − GROUND_K[0]) / span x 255 (the GPU interpolates k between
// the cell centres) | null (no grid: k = 1 everywhere, the uniform switch off)
// groundKGridSteps = THE grid, a step per `every` cells (R8 M6b: the whole
// grid was one 65-79 ms task in the Map dwell); groundKGrid = its drain
export function groundKGrid(R, cMid) {
  const it = groundKGridSteps(R, cMid);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* groundKGridSteps(R, cMid, every = 4096) {
  if (!R) return null;
  const n = R.w * R.h, out = new Uint8Array(2 * n), D = R.data, span = GROUND_K[1] - GROUND_K[0];
  const at = (i, ch) => (D[3 * i + ch] ? dec(R.enc[['r', 'g', 'b'][ch]], D[3 * i + ch]) : null);
  for (let i = 0; i < n; i++) {
    if (i && i % every === 0) yield;
    const s = { relief: at(i, 0), scale: at(i, 1), contrast: at(i, 2) };
    GROUND_K_LAYERS.forEach((key, c) => {
      out[2 * i + c] = Math.round((groundK(key, s, cMid[key]) - GROUND_K[0]) / span * 255);
    });
  }
  return { w: R.w, h: R.h, cell: R.cell, data: out };
}
